import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { Calendar, User, ArrowLeft, Clock, Target, Database, Shield, Zap, Users, HeadphonesIcon, Calculator, FileText, Phone, MessageSquare, CheckCircle, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RainbowButton } from "@/components/ui/rainbow-button";
import Navbar from '@/components/Layout/Navbar';
import Footer from '@/components/Layout/Footer';

const Blog = () => {
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "AI Consulting Services: A Practical Guide for UK Businesses",
    "description": "Practical AI consulting services for UK SMEs and mid-market firms. Roadmaps, workflow automation, chat and call agents, and AI powered websites delivered with measurable ROI.",
    "author": {
      "@type": "Organization",
      "name": "Njord AI Automation Agency",
      "url": "https://www.njordai.co.uk/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Njord AI Automation Agency",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png"
      }
    },
    "datePublished": "2025-09-16",
    "dateModified": "2025-09-16",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.njordai.co.uk/blog/ai-consulting-services-practical-guide-uk-businesses"
    },
    "image": "https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png",
    "articleSection": "AI Consulting",
    "keywords": "AI consulting services, UK businesses, workflow automation, AI implementation, business process automation, Northamptonshire, Midlands"
  };

  return (
    <>
      <Helmet>
        <title>AI Consulting Services for UK Businesses | Njord</title>
        <meta name="description" content="Practical AI consulting services for UK SMEs and mid-market firms. Roadmaps, workflow automation, chat and call agents, and AI powered websites delivered with measurable ROI." />
        <meta name="keywords" content="AI consulting services, UK businesses, workflow automation, AI implementation, business process automation, Northamptonshire, Midlands, SME AI solutions" />
        <meta property="og:title" content="AI Consulting Services: A Practical Guide for UK Businesses" />
        <meta property="og:description" content="Practical AI consulting services for UK SMEs and mid-market firms. Roadmaps, workflow automation, chat and call agents, and AI powered websites delivered with measurable ROI." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.njordai.co.uk/blog/ai-consulting-services-practical-guide-uk-businesses" />
        <meta property="og:image" content="https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Consulting Services: A Practical Guide for UK Businesses" />
        <meta name="twitter:description" content="Practical AI consulting services for UK SMEs and mid-market firms. Roadmaps, workflow automation, chat and call agents, and AI powered websites delivered with measurable ROI." />
        <meta name="twitter:image" content="https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png" />
        <link rel="canonical" href="https://www.njordai.co.uk/blog/ai-consulting-services-practical-guide-uk-businesses" />
        
        {/* Blog Schema */}
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
      </Helmet>
      
      <Navbar />
      
      <div className="bg-white min-h-screen">
        {/* Header */}
        <div className="bg-gradient-to-b from-gray-50 to-white py-20 pt-32">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link 
                to="/" 
                className="inline-flex items-center text-primary hover:text-primary-600 mb-8 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold text-black mb-6">
                  AI consulting services for UK SMEs: where to start
                </h1>
                
                <div className="flex items-center space-x-6 text-gray-600 mb-8">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>September 16, 2025</span>
                  </div>
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    <span>Njord AI Team</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>12 min read</span>
                  </div>
                </div>
                
                <p className="text-xl text-gray-700 leading-relaxed">
                  A comprehensive guide to AI consulting services for UK small and medium-sized businesses.
                </p>
                
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="prose prose-lg max-w-none mb-12"
              >
                {/* Hero Image and Intro Text Layout */}
                <div className="flex flex-col lg:flex-row gap-8 items-start mb-12">
                  <div className="lg:w-2/5 flex-shrink-0">
                    <img 
                      src="https://lbfszmtuhlnsahiqsxmf.supabase.co/storage/v1/object/public/njord_blog_pictures/ai-consulting-services-uk.png"
                      alt="AI consulting services for UK businesses - workflow automation and business transformation"
                      className="w-full rounded-lg shadow-lg"
                    />
                  </div>
                  <div className="lg:w-3/5">
                    <p className="text-gray-700 leading-relaxed text-xl">
                      For many small and medium-sized businesses in the UK, "AI" can feel like a buzzword reserved for big tech. In reality, AI consulting services are already helping SMEs reduce costs, save time, and compete with much larger organisations. The challenge is knowing where to begin. This guide explains what AI consulting involves, the benefits it brings, and how to take your first steps.
                    </p>
                  </div>
                </div>

                {/* What are AI consulting services? */}
                <section className="mb-12">
                  <h2 className="text-3xl font-bold text-black mb-6 flex items-center">
                    What are AI consulting services?
                  </h2>

                  <p className="text-gray-700 leading-relaxed mb-6">
                    AI consulting is about applying artificial intelligence in ways that solve real business problems. Rather than pushing software, an AI consultant studies your current workflows and identifies where automation or machine learning can deliver measurable value.
                  </p>

                  <p className="text-gray-700 leading-relaxed mb-4">Typical services include:</p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Process analysis:</strong> <span className="text-gray-700">spotting repetitive, manual tasks that drain staff time</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Roadmap creation:</strong> <span className="text-gray-700">setting out a 90-day plan with achievable goals</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Tool selection:</strong> <span className="text-gray-700">choosing between existing platforms, low-code tools, or custom AI solutions</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Implementation:</strong> <span className="text-gray-700">integrating, testing, and training staff</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Governance:</strong> <span className="text-gray-700">ensuring projects remain GDPR-compliant and secure</span>
                      </div>
                    </li>
                  </ul>
                </section>

                {/* Why SMEs should care */}
                <section className="mb-12">
                  <h2 className="text-3xl font-bold text-black mb-6">Why SMEs should care</h2>
                  
                  <p className="text-gray-700 leading-relaxed mb-6">
                    AI is not only for large enterprises. Affordable solutions already exist for SMEs, with some of the quickest returns coming from small but targeted changes.
                  </p>

                  <p className="text-gray-700 leading-relaxed mb-4">Examples include:</p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">AI chat agents</strong> <span className="text-gray-700">qualify leads and route them to the right person</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Document automation and reporting</strong> <span className="text-gray-700">free staff from manual work</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Lead outreach bots</strong> <span className="text-gray-700">personalise and schedule follow-ups automatically</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong className="text-black">Compliance documents</strong> <span className="text-gray-700">such as risk assessments and training logs generated on demand</span>
                      </div>
                    </li>
                  </ul>
                </section>

                {/* Common mistakes to avoid */}
                <section className="mb-12">
                  <h2 className="text-3xl font-bold text-black mb-6">Common mistakes to avoid</h2>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Buying tools without a clear strategy</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Overlooking team adoption and training</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Relying on poor-quality data</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">Ignoring GDPR or sector regulations</span>
                    </li>
                  </ul>
                </section>

                {/* How to get started with AI consulting */}
                <section className="mb-12">
                  <h2 className="text-3xl font-bold text-black mb-6">How to get started with AI consulting</h2>
                  
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start">
                      <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 text-sm font-semibold">
                        1
                      </div>
                      <div>
                        <strong className="text-black">Identify quick wins:</strong> <span className="text-gray-700">start with rule-based, repetitive tasks</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 text-sm font-semibold">
                        2
                      </div>
                      <div>
                        <strong className="text-black">Work with an expert:</strong> <span className="text-gray-700">a consultant can design a roadmap and keep projects secure</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 text-sm font-semibold">
                        3
                      </div>
                      <div>
                        <strong className="text-black">Start small, measure results:</strong> <span className="text-gray-700">pilots should deliver results in 4–6 weeks</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 text-sm font-semibold">
                        4
                      </div>
                      <div>
                        <strong className="text-black">Scale responsibly:</strong> <span className="text-gray-700">extend what works, always with proper documentation</span>
                      </div>
                    </li>
                  </ul>
                </section>

                {/* Local spotlight */}
                <section className="mb-12 bg-gray-50 p-8 rounded-lg">
                  <h2 className="text-3xl font-bold text-black mb-6 flex items-center">
                    <MapPin className="h-8 w-8 text-primary mr-3" />
                    Local spotlight: Northamptonshire and the Midlands
                  </h2>
                  
                  <p className="text-gray-700 leading-relaxed">
                    AI consulting is not limited to London. Businesses across Northamptonshire and the Midlands are already adopting AI. From manufacturers using <strong>workflow automation</strong> to service companies deploying <strong>AI call bots</strong>, the region is seeing rapid adoption.
                  </p>
                </section>

                {/* FAQs */}
                <section className="mb-12">
                  <h2 className="text-3xl font-bold text-black mb-6">FAQs</h2>
                  
                  <div className="space-y-6">
                    {[
                      {
                        question: "What is the role of an AI consultant?",
                        answer: "They identify value opportunities, design solutions, manage implementation, and ensure compliance."
                      },
                      {
                        question: "How quickly can SMEs see results?",
                        answer: "Most projects deliver improvements in 4–6 weeks."
                      },
                      {
                        question: "Is AI consulting expensive?",
                        answer: "No. Many projects use existing tools, keeping costs manageable."
                      },
                      {
                        question: "Do I need new software?",
                        answer: "Often not. Consultants can integrate AI into your current stack."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleFaq(index)}
                          className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                        >
                          <h3 className="text-lg font-semibold text-black pr-4">{faq.question}</h3>
                          {openFaq === index ? (
                            <ChevronUp className="h-5 w-5 text-primary flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-primary flex-shrink-0" />
                          )}
                        </button>
                        {openFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="px-6 pb-6"
                          >
                            <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Next step */}
                <section className="bg-white border-2 border-primary rounded-lg p-8 text-center shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 className="text-3xl font-bold mb-4 text-black">Next step</h2>
                  <p className="text-lg mb-6 text-gray-700">
                    If you're exploring AI consulting services, Njord offers a discovery sprint to map opportunities, outline a 90-day roadmap, and deliver a pilot that proves value before scaling.
                  </p>
                  <RainbowButton>
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Start Your Discovery Sprint
                  </RainbowButton>
                </section>

                {/* References */}
                <section className="mb-12 bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-black mb-4">References</h3>
                  <ul className="space-y-2">
                    <li>
                      <a href="https://www.gov.uk/government/publications/national-ai-strategy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-600 transition-colors">
                        UK Government – National AI Strategy
                      </a>
                    </li>
                    <li>
                      <a href="https://www.consumeredge.com/resources/insights/ai-brand-spotlight-q2-report/?utm_term=mckinsey%20state%20of%20ai%20report%202025&utm_campaign=Search_Corporate&utm_source=google&utm_medium=cpc&hsa_acc=4353012362&hsa_cam=20557452946&hsa_grp=171049005801&hsa_ad=713678306534&hsa_src=g&hsa_tgt=kwd-2433434191102&hsa_kw=mckinsey%20state%20of%20ai%20report%202025&hsa_mt=p&hsa_net=adwords&hsa_ver=3&gad_source=1&gad_campaignid=20557452946&gclid=CjwKCAjwlaTGBhANEiwAoRgXBXMoUMFzv0S_r1e8idOKKC7mVpiK0UHmaGrMMfZa3UfLE7UzbNslahoCIbUQAvD_BwE" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-600 transition-colors">
                        McKinsey: The state of AI in 2025
                      </a>
                    </li>
                  </ul>
                </section>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Blog;